import _ from 'lodash';
import { createContext, useEffect, useRef, useState } from 'react';

import { readDraft, writeDraft } from './draft';

export const LabelContext = createContext();

const emptyLabelInfo = {
  productItem: {
    title: '',
    brand: '',
    merchantCode: '',
    hasVariant: false,
    detail: '',
    /** Сервер рүү илгээх категорийн КОД (ж: "BEV") */
    category: '',
    /** Хэрэглэгчид харуулах категорийн НЭР — зөвхөн дэлгэцэнд, сервер рүү явахгүй */
    categoryName: '',
    unit: 'unit',
    hasReturn: false,
    isCBM: false,
    cbm: '',
    image: '',
    imageOther: [],
    long: '',
    wide: '',
    height: '',
    volume: '',
    color: '',
    size: '',
    type: '',
    weight: '',
    baseSku: '',
    barCode: '',
    barcodes: [],
    price: '',
    volumeUnit: 'cm',
    weightUnit: 'g',
    stock: '',
    minStock: '',
    reorderQuantity: '',
    willExpire: false,
    isFragile: false,
  },
  productVersion: {
    color: '',
    size: '',
    type: '',
    productSKU: '',
    price: '',
    stock: '',
    image: undefined,
    images: [],
  },
};

const emptyDetailInfo = {
  skuShortName: '',
  skuLongName: '',
  barCode: '',
  skuCode: '',
  range: [],
  hasRange: false,
};

/**
 * @param {string} [props.draftKey] sessionStorage-ийн key. Дамжуулбал бөглөсөн
 * мэдээлэл refresh хийхэд сэргэнэ. Дамжуулаагүй бол хадгалахгүй.
 */
export const ProductProvider = ({ draftKey, children }) => {
  // Хуудас refresh хийхэд бөглөсөн мэдээлэл сэргэхийн тулд эхлэхдээ л уншина
  const draft = useRef(readDraft(draftKey)).current;

  const [labelInfo, setlabelInfo] = useState(draft.labelInfo ?? emptyLabelInfo);
  const [detailInfo, setDetailInfo] = useState(draft.detailInfo ?? emptyDetailInfo);
  const [variantList, setVariantList] = useState(draft.variantList ?? []);
  const [productBaseSku, setProductBaseSku] = useState(draft.productBaseSku ?? null);
  const [productVolume, setProductVolume] = useState(draft.productVolume ?? 0);
  const [isErlian, setIsErlian] = useState(draft.isErlian ?? false);
  const [otherImages, setOtherImages] = useState(draft.otherImages ?? []);
  const [step1Values, setStep1Values] = useState(draft.step1Values ?? {});
  /**
   * Хэрэглэгчийн батласан хамгийн сүүлийн алхам. Refresh хийсний дараа `?step=`
   * -ээр батлаагүй алхам руу шууд орохоос сэргийлнэ (ProductStep.js).
   */
  const [maxStep, setMaxStep] = useState(draft.maxStep ?? 0);

  // Төлөв өөрчлөгдөх бүрд draft-ыг шинэчилнэ
  useEffect(() => {
    writeDraft(draftKey, {
      labelInfo,
      detailInfo,
      variantList,
      productBaseSku,
      productVolume,
      isErlian,
      otherImages,
      step1Values,
      maxStep,
    });
  }, [
    draftKey,
    labelInfo,
    detailInfo,
    variantList,
    productBaseSku,
    productVolume,
    isErlian,
    otherImages,
    step1Values,
    maxStep,
  ]);

  const setProductItem = (prop) => (event) => {
    if (event?.target) {
      setlabelInfo({
        ...labelInfo,
        productItem: { ...labelInfo.productItem, [prop]: event.target.value },
      });
    } else {
      setlabelInfo({
        ...labelInfo,
        productItem: { ...labelInfo.productItem, [prop]: event },
      });
    }
  };

  const setProductVersion = (prop) => (event) => {
    if (event.target.value) {
      setlabelInfo({
        ...labelInfo,
        productVersion: {
          ...labelInfo.productVersion,
          [prop]: event.target.value,
        },
      });
    } else {
      setlabelInfo({
        ...labelInfo,
        productVersion: { ...labelInfo.productVersion, [prop]: event },
      });
    }
  };

  const onChangeVariantList = (prop) => {
    setVariantList({
      ...variantList,
      prop,
    });
  };
  const onChangeBaseSku = (sku) => {
    setProductBaseSku(sku);
  };
  const onChangeProductVolume = (volume) => {
    setProductVolume(volume);
  };

  const changeLevelInfo = (key, subKey, value) => {
    const clone = _.cloneDeep(labelInfo);
    clone[key][subKey] = value;
    setlabelInfo(clone);
  };

  /**
   * Барааны хэд хэдэн талбарыг НЭГ шинэчлэлээр бичнэ. `changeLevelInfo`-г
   * дараалуулан дуудвал хоёулаа нэг л (хуучин) төлвөөс хуулбарладаг тул сүүлийнх
   * нь өмнөхийг дардаг — энд функцэн хэлбэрээр бичиж түүнээс сэргийлнэ.
   */
  const changeProductItems = (values) => {
    setlabelInfo((prev) => ({
      ...prev,
      productItem: { ...prev.productItem, ...values },
    }));
  };
  const changeDetailInfo = (key, value) => {
    const clone = _.cloneDeep(detailInfo);
    clone[key] = value;
    setDetailInfo(clone);
  };
  const onSetOtherImages = (fileList) => {
    setOtherImages([...fileList]);
  };

  return (
    <LabelContext.Provider
      value={{
        // Бараа үүссэний дараа ProductCheck нь ЯГ энэ key-г цэвэрлэнэ
        draftKey,
        setProductItem,
        setProductVersion,
        onChangeVariantList,
        labelInfo,
        detailInfo,
        variantList,
        onChangeBaseSku,
        productBaseSku,
        onChangeProductVolume,
        productVolume,
        changeLevelInfo,
        changeProductItems,
        changeDetailInfo,
        otherImages,
        onSetOtherImages,
        isErlian,
        setIsErlian,
        step1Values,
        setStep1Values,
        maxStep,
        setMaxStep,
      }}
    >
      {children}
    </LabelContext.Provider>
  );
};
